import { useState } from 'react'
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  CommitmentLetterPrepareResponse,
  CommitmentLetterVendor,
} from '@/api/commitmentLetters'
import { useBuyStrategyCodes } from '@/hooks/useCltReference'
import { formatIsoDate, vendorLabel } from './utils'
import { PrepareIssues } from './PrepareIssues'

type StrategyChangeHandler = (vendorKey: string, code: string | null) => void

interface VendorStrategySelectProps {
  vendor: CommitmentLetterVendor
  calendarCodes: string[]
  calendarLabels: Record<string, string[]>
  disabled: boolean
  onChange: StrategyChangeHandler
}

/** Prefilled (derived) Strategy Code for one vendor; the planner can override it. */
function VendorStrategySelect({
  vendor,
  calendarCodes,
  calendarLabels,
  disabled,
  onChange,
}: VendorStrategySelectProps) {
  const { data: codes = [] } = useBuyStrategyCodes()
  // The calendar's group label wins so the dropdown matches the transit table
  // and the letter; the reference-data label is only a fallback.
  const labelOf = (c: { code: string; label: string }) =>
    calendarLabels[c.code]?.join(' / ') || c.label
  const value = vendor.strategy_code ?? ''
  const options =
    codes.some(c => c.code === value) || !value
      ? codes
      : [{ id: value, code: value, label: '' }, ...codes]
  const isOverride =
    !!vendor.default_strategy_code && value !== vendor.default_strategy_code

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <Label className="shrink-0">Strategy Code</Label>
        <Select
          value={value}
          disabled={disabled}
          onValueChange={code =>
            onChange(
              vendor.vendor_key,
              code === vendor.default_strategy_code ? null : code
            )
          }
        >
          <SelectTrigger className="h-9 w-[360px]">
            <SelectValue placeholder="Select a strategy code" />
          </SelectTrigger>
          <SelectContent>
            {options.map(c => (
              <SelectItem key={c.code} value={c.code}>
                {labelOf(c) ? `${c.code} – ${labelOf(c)}` : c.code}
                {!calendarCodes.includes(c.code) && (
                  <span className="text-muted-foreground">
                    {' '}
                    (no calendar dates)
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isOverride && (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(vendor.vendor_key, null)}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to default ({vendor.default_strategy_code})
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Default from plan type and conveyable: Seasonal → 90, Core conveyable →
        3/4/7, Core non-conveyable → 1.
      </p>
    </div>
  )
}

interface VendorTransitProps {
  vendor: CommitmentLetterVendor
  calendarCodes: string[]
  calendarLabels: Record<string, string[]>
  isRefreshing: boolean
  onStrategyChange: StrategyChangeHandler
}

function VendorTransit({
  vendor,
  calendarCodes,
  calendarLabels,
  isRefreshing,
  onStrategyChange,
}: VendorTransitProps) {
  return (
    <div className="flex flex-col gap-3">
      <VendorStrategySelect
        vendor={vendor}
        calendarCodes={calendarCodes}
        calendarLabels={calendarLabels}
        disabled={isRefreshing}
        onChange={onStrategyChange}
      />

      {!vendor.strategy_has_calendar && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Blank ship dates</AlertTitle>
          <AlertDescription>
            Strategy Code {vendor.strategy_code} is not in the Supply Chain
            calendar, so this letter will have blank dates that you will need to
            update.
          </AlertDescription>
        </Alert>
      )}

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">SKUs</dt>
        <dd>{vendor.line_item_count}</dd>
        <dt className="text-muted-foreground">Ship points</dt>
        <dd>{vendor.ship_points.join(', ') || '—'}</dd>
        <dt className="text-muted-foreground">Ports</dt>
        <dd>{vendor.ports.map(p => p.name).join(' / ') || '—'}</dd>
        <dt className="text-muted-foreground">Strategies</dt>
        <dd>{vendor.strategies.join(' / ') || '—'}</dd>
        <dt className="text-muted-foreground">Ship window</dt>
        <dd>
          {vendor.ship_window_start
            ? `${formatIsoDate(vendor.ship_window_start)} - ${formatIsoDate(vendor.ship_window_end)}`
            : '—'}
        </dd>
      </dl>

      {vendor.unmapped_ship_points.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Ship points without transit dates</AlertTitle>
          <AlertDescription>
            {vendor.unmapped_ship_points.join(', ')}{' '}
            {vendor.unmapped_ship_points.length === 1 ? 'is' : 'are'} not in the
            transit tables, so the letter has no ship dates for{' '}
            {vendor.unmapped_ship_points.length === 1 ? 'it' : 'them'}.
          </AlertDescription>
        </Alert>
      )}

      {vendor.transit_rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No transit dates for this vendor.
        </p>
      ) : (
        <div className="max-h-[320px] overflow-auto rounded-md border border-[#d6dae1]">
          <Table>
            <TableHeader className="sticky top-0 bg-white">
              <TableRow>
                <TableHead>Origin</TableHead>
                <TableHead>Strategy</TableHead>
                <TableHead>Destination DC</TableHead>
                <TableHead>Store Set</TableHead>
                <TableHead>Not Before</TableHead>
                <TableHead>Not After</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendor.transit_rows.map(row => (
                <TableRow
                  key={`${row.origin_code}-${row.strategy}-${row.destination_dc}`}
                >
                  <TableCell>{row.origin_name}</TableCell>
                  <TableCell>{row.strategy}</TableCell>
                  <TableCell>{row.destination_dc}</TableCell>
                  <TableCell>{formatIsoDate(row.store_set)}</TableCell>
                  <TableCell>{formatIsoDate(row.not_before)}</TableCell>
                  <TableCell>{formatIsoDate(row.not_after)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

interface VendorPickerProps extends Omit<VendorTransitProps, 'vendor'> {
  vendors: CommitmentLetterVendor[]
}

function VendorPicker({ vendors, ...transitProps }: VendorPickerProps) {
  const [vendorKey, setVendorKey] = useState(vendors[0].vendor_key)
  const vendor = vendors.find(v => v.vendor_key === vendorKey) ?? vendors[0]

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Label className="shrink-0">Vendor</Label>
        <Select value={vendor.vendor_key} onValueChange={setVendorKey}>
          <SelectTrigger className="h-9 w-[360px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {vendors.map(v => (
              <SelectItem key={v.vendor_key} value={v.vendor_key}>
                {vendorLabel(v)}
                {v.transit_rows.length === 0 && (
                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <VendorTransit vendor={vendor} {...transitProps} />
    </div>
  )
}

interface StepTransitProps {
  data: CommitmentLetterPrepareResponse | undefined
  isLoading: boolean
  isRefreshing: boolean
  onStrategyChange: StrategyChangeHandler
  vendorsMissingDates: CommitmentLetterVendor[]
  acknowledgedMissing: boolean
  onAcknowledgeMissing: (value: boolean) => void
}

export function StepTransit({
  data,
  isLoading,
  isRefreshing,
  onStrategyChange,
  vendorsMissingDates,
  acknowledgedMissing,
  onAcknowledgeMissing,
}: StepTransitProps) {
  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {data.event
          ? `Event ${data.event.event_code ?? data.event.name}${data.event.year ? ` (${data.event.year})` : ''}`
          : 'No calendar event'}
        {data.ship_milestone ? ` · Not After = ${data.ship_milestone}` : ''}
        {' · '}One letter per vendor ({data.vendors.length}).
      </p>

      <PrepareIssues data={data} />

      {data.vendors.length > 0 && (
        <VendorPicker
          vendors={data.vendors}
          calendarCodes={data.calendar_strategy_codes}
          calendarLabels={data.calendar_strategy_labels ?? {}}
          isRefreshing={isRefreshing}
          onStrategyChange={onStrategyChange}
        />
      )}

      {vendorsMissingDates.length > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={acknowledgedMissing}
            onCheckedChange={onAcknowledgeMissing}
          />
          Generate anyway — {vendorsMissingDates.length} vendor(s) will get an
          empty transit calendar.
        </label>
      )}
    </div>
  )
}
